import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import * as Sentry from '@sentry/react'
import { matchPath } from 'react-router'
import { z, type ZodError } from 'zod'
import { isArray, isObject } from 'lodash-es'
import axios from 'axios'
import config from '@/config'
import type { AvjErrorType } from '@/connect-types/backend/service'
import {
  type ApiUserRefreshTokenResponse,
  getToken,
  setToken,
} from './AuthTokenHelper'

const customAxios = axios.create({
  //adapter: 'fetch' in window && fetchAdapter,
  timeout: 1000 * 30,
  validateStatus: (status: any) => {
    return status >= 200 && status < 303
  },
})

export function isAxiosError<T>(error: unknown): error is AxiosError<T> {
  return (error as AxiosError)?.isAxiosError !== undefined
}

export const isAjvError = <T extends AvjErrorType>(
  error: unknown
): error is AxiosError<T> => {
  if (!isAxiosError<AvjErrorType>(error)) return false
  if (error.response.data?.context) return true
  return false
}

export const isZodError = <T extends ZodError>(
  error: unknown
): error is AxiosError<T> => {
  if (!isAxiosError<ZodError>(error)) return false
  if (
    !('message' in error.response.data) ||
    !Array.isArray(error.response?.data.message)
  ) {
    return false
  }
  return true
}

/**
 * Formats Zod validation errors from a 422 response into user-friendly messages
 * @param error - The Axios error containing Zod validation errors
 * @returns A formatted error message string
 */
export const formatZodValidationErrors = (
  error: AxiosError<{ message: { path: string[]; message: string }[] }>
): string => {
  if (
    !error.response?.data?.message ||
    !Array.isArray(error.response.data.message)
  ) {
    return 'Validation failed'
  }

  const errors = error.response.data.message

  // If there's only one error, return just the message
  if (errors.length === 1) {
    const fieldName = errors[0].path.join('.')
    return `${capitalizeFirstLetter(fieldName)}: ${errors[0].message}`
  }

  // If there are multiple errors, format as a list
  const errorMessages = errors.map((err) => {
    const fieldName = err.path.join('.')
    return `${capitalizeFirstLetter(fieldName)}: ${err.message}`
  })

  return errorMessages.join('; ')
}

/**
 * Helper function to capitalise the first letter of a string
 */
const capitalizeFirstLetter = (str: string): string => {
  if (!str) return ''
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export const isMorpheusError = <T>(error: unknown): error is AxiosError<T> => {
  if (!isAxiosError<{ stack: any; method: any }>(error)) return false
  console.log('error-here', error.response.data)
  if ('stack' in error.response.data && 'method' in error.response.data)
    return true
  return false
}

/**
 * Checks if the given error is a Morpheus Zod validation error (422 response with validation message array)
 * @param error - The error to check
 * @returns A boolean indicating whether the error is a Morpheus Zod validation error
 */
export const isMorpheusZodValidationError = (
  error: unknown
): error is AxiosError<{
  stack: string
  code: number
  message: {
    origin?: string
    code: string
    format?: string
    pattern?: string
    path: string[]
    message: string
  }[]
  url: string
  method: string
}> => {
  // Check it's an axios error
  if (!isAxiosError(error)) {
    return false
  }

  // Check status code is 422
  if (error.response?.status !== 422) {
    return false
  }

  // Validate the data structure
  const dataParser = z
    .object({
      stack: z.string(),
      code: z.number(),
      message: z.array(
        z.object({
          origin: z.string().optional(),
          code: z.string(),
          format: z.string().optional(),
          pattern: z.string().optional(),
          path: z.array(z.string()),
          message: z.string(),
        })
      ),
      url: z.string(),
      method: z.string(),
    })
    .safeParse(error.response.data)

  return dataParser.success
}

export const isBackendError = <T>(error: unknown): error is AxiosError<T> => {
  if (!isAxiosError<{ detail: any }>(error)) return false
  if (error.response.data?.detail) return true
  return false
}

export const isZionError = <T>(error: unknown): error is AxiosError<T> => {
  if (!isAxiosError(error)) return false
  if (!error.response.config?.url?.includes('zion')) return false
  if (error.response.data?.errors) return true
  return false
}

/**
 * Checks if the given error is an Axios error with a 422 status code and a specific validation error structure.
 *
 * @param error - The error to check.
 * @returns A boolean indicating whether the error is an Axios error with a 422 status code and a validation error structure.
 */
export const isServiceValidationError = (
  error: unknown
): error is AxiosError<{
  message: string
  errors: Record<string, string[]>
}> => {
  // check it's an axios error.
  if (!isAxiosError(error)) {
    return false
  }

  // check status code.
  if (error.response?.status !== 422) {
    return false
  }

  // validate the data.
  const dataParser = z
    .object({
      message: z.string(),
      errors: z.record(z.string(), z.array(z.string())),
    })
    .safeParse(error.response.data)

  // return.
  return dataParser.success
}

export const isServiceError = <T>(error: unknown): error is AxiosError<T> => {
  if (!isAxiosError<{ message: any; errors: unknown[] }>(error)) return false
  if (error.response.data?.message) return true
  if (error.response.data?.errors) return true
  return false
}

const noneAuthApiPaths = [
  '/public/*',
  '/members/me/member',
  '/members/me/logout',
  '/oauth/logout',
  '/oauth/token',
  '/',
]

const doesRouteMatchIgnorePath = (path: string) => {
  return (
    noneAuthApiPaths
      .map((item: any) => matchPath(item, path.replace(/^.*\/\/[^\/]+/, '')))
      .filter((item: any) => Boolean(item)).length >= 1
  )
}

customAxios.interceptors.request.use((config: any) => {
  const token = getToken()

  if (!token) {
    return config
  }

  if (token) {
    // Don't add Bearer token to signed s3 urls, for csv uploads
    if (isS3Url(config?.url ?? '')) {
      return config
    }
    config.headers.Authorization = `${token.tokenType} ${token.accessToken}`
  }

  return config
})

customAxios.interceptors.response.use(
  (response: any) => response,
  (error: any) => {
    const errorResponse = error.response
    if (axios.isAxiosError(error)) {
      if (error.code === 'ECONNABORTED') {
        Sentry.captureException(error)
      }
    }

    if (
      isTokenExpiredError(errorResponse) &&
      !doesRouteMatchIgnorePath(error.config?.url ?? '')
    ) {
      return resetTokenAndReattemptRequest(error)
    }
    if (error?.response?.status >= 400 && error?.response?.status <= 500) {
      // retry.fail(error)
    }

    // If the error is due to other reasons, we just throw it back to axios
    return Promise.reject(error)
  }
)

export const poster = async (
  url: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
) => customAxios.post(url, data, config)
export const getter = async (url: string, config?: AxiosRequestConfig) =>
  customAxios.get(url, config)
export const putter = async (
  url: string,
  data?: Record<string, unknown>,
  config?: AxiosRequestConfig
) => customAxios.put(url, data, config)
export const deler = async (url: string, config?: AxiosRequestConfig) =>
  customAxios.delete(url, config)

function isTokenExpiredError(errorResponse: any): boolean {
  // Your own logic to determine if the error is due to JWT token expired returns a boolean value
  if (errorResponse && errorResponse.status === 401) return true

  return false
}

let isAlreadyFetchingAccessToken = false

// This is the list of waiting requests that will retry after the JWT refresh complete
let subscribers: any = []

// `s` (e.g. s=invite) rides as its own param straight through to accounts,
// which uses it to vary the login page copy.
const buildLogoutRedirect = () => {
  const s = new URLSearchParams(window.location.search).get('s')
  return `/oauth/logout?state=${window.location.pathname}${s ? `&s=${s}` : ''}`
}

const resetTokenAndReattemptRequest = async (error: any) => {
  try {
    const { response: errorResponse } = error
    const token = getToken()

    if (!token) {
      // We can't refresh, throw the error anyway

      window.location.href = buildLogoutRedirect()

      return Promise.reject(error)
    }

    /* Proceed to the token refresh procedure
    We create a new Promise that will retry the request,
    clone all the request configuration from the failed
    request in the error object. */
    const retryOriginalRequest = new Promise((resolve: any) => {
      /* We need to add the request retry to the queue
    since there another request that already attempt to
    refresh the token */
      addSubscriber((access_token: string) => {
        errorResponse.config.headers.Authorization = `Bearer ${access_token}`
        resolve(axios(errorResponse.config))
      })
    })

    if (!isAlreadyFetchingAccessToken) {
      isAlreadyFetchingAccessToken = true
      const response = await axios.post<ApiUserRefreshTokenResponse>(
        `${config.url.morpheus}/oauth/token`,
        {
          grant_type: 'refresh_token',
          client_id: config.auth.clientId,
          refresh_token: token.refreshToken,
        }
      )

      if (!response.data) {
        return Promise.reject(error)
      }

      const { access_token, expires_in, refresh_token, scope, token_type } =
        response.data
      const userCredentials = {
        accessToken: access_token,
        expiresIn: expires_in,
        refreshToken: refresh_token,
        scope,
        tokenType: token_type,
        expiresAt: expires_in ? Date.now() + expires_in * 1000 : null,
      }

      setToken(userCredentials) // save the newly refreshed token for other requests to use
      isAlreadyFetchingAccessToken = false
      onAccessTokenFetched(access_token)
    }

    return retryOriginalRequest
  } catch (err) {
    if (isAxiosError<{ error: unknown }>(err) && err.response) {
      console.log(err.response.status, err.response.data, 'err')
      if (err.response.status === 400 && 'error' in err.response.data) {
        window.location.href = buildLogoutRedirect()
      }
    }
    return Promise.reject(err)
  }
}

function onAccessTokenFetched(access_token: string) {
  // When the refresh is successful, we start retrying the requests one by one and empty the queue
  subscribers.forEach((callback: any) => callback(access_token))
  subscribers = []
}

function addSubscriber(callback: any) {
  subscribers.push(callback)
}

const isS3Url = (url: string) => {
  return new RegExp('^https://.*s3.*.amazonaws.com*').test(url)
}

export { customAxios }
